import { memo } from "react";

import { type IconName } from "@components/Icon";
import { ProgressBar } from "@components/ProgressBar";
import { Text } from "@components/Text";
import {
  type ProgressBarThresholdItem,
  type CartTieredProgressBarSettings,
} from "@root/tablescape/state";
import { tw } from "classnames";

type Props = {
  colorGradient: CartTieredProgressBarSettings["colorGradient"];
  hasReachedInitialThreshold: boolean;
  iconName?: IconName;
  progressPercentage: number;
  progressText: string | undefined;
  thresholdItems: ProgressBarThresholdItem[];
  titleText: string | undefined;
};

const styles = {
  container: tw(
    "border-0",
    "border-b",
    "border-solid",
    "border-divider",
    "w-full",
    "px-6",
    "py-2.5",
    "md:py-4",
    "bg-background-secondary"
  ),
  text: (hasReachedThresholdInSingleTier: boolean) =>
    tw("mb-3", {
      "text-misc-bright-blue": hasReachedThresholdInSingleTier,
    }),
  titleText: (hasReachedInitialThresholdInMultiTiers: boolean) =>
    tw("transition-all", "duration-200", {
      "text-misc-bright-blue": hasReachedInitialThresholdInMultiTiers,
    }),
};

const CartTieredProgressBarViewBase = ({
  colorGradient,
  hasReachedInitialThreshold,
  iconName,
  progressPercentage,
  progressText,
  thresholdItems,
  titleText,
}: Props) => {
  return (
    <div className={styles.container}>
      {titleText && (
        <Text
          className={styles.titleText(
            hasReachedInitialThreshold && thresholdItems.length > 1
          )}
          size={{ sm: "sm", md: "md" }}
        >
          {titleText}
        </Text>
      )}
      {progressText && (
        <Text
          className={styles.text(
            hasReachedInitialThreshold && thresholdItems.length === 1
          )}
          size={{ sm: "sm", md: "md" }}
        >
          {progressText}
        </Text>
      )}
      <ProgressBar
        border
        color={colorGradient}
        iconName={iconName}
        progressPercentage={progressPercentage}
        size="lg"
        thresholdItems={thresholdItems}
        type="threshold"
      />
    </div>
  );
};

export const CartTieredProgressBarView = memo(CartTieredProgressBarViewBase);
